import React, { useState } from "react";
import { Link } from "react-router-dom";
import { publicApi } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, Upload, FileCheck2, Car, ShieldCheck } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const MAX_VEHICLE_AGE = 4;
const CURRENT_YEAR = new Date().getFullYear();
const MIN_VEHICLE_YEAR = CURRENT_YEAR - MAX_VEHICLE_AGE;

const inputCls = "w-full bg-white border border-[#d4d4d8] rounded px-3 py-3 text-sm text-[#18181b] placeholder-[#a1a1aa] focus:outline-none focus:border-[#D4AF37]";
const labelCls = "block text-[10px] uppercase tracking-[0.22em] text-[#52525b] mb-1.5";

export default function DriveWithUs() {
  const [form, setForm] = useState({
    full_name: "", phone: "", email: "",
    vehicle_make: "", vehicle_model: "", vehicle_year: "", vehicle_color: "",
  });
  const [files, setFiles] = useState({ license: null, insurance: null, chauffeur: null });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setFile = (k) => (e) => setFiles((f) => ({ ...f, [k]: e.target.files?.[0] || null }));

  const submit = async (e) => {
    e.preventDefault();
    const year = parseInt(form.vehicle_year, 10);
    if (!year || year < MIN_VEHICLE_YEAR || year > CURRENT_YEAR + 1) {
      toast.error(`Vehicle must be no more than ${MAX_VEHICLE_AGE} years old (${MIN_VEHICLE_YEAR} or newer)`);
      return;
    }
    if (!files.license || !files.insurance) {
      toast.error("Driver's license and certificate of insurance are required");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append("license_file", files.license);
      fd.append("insurance_file", files.insurance);
      if (files.chauffeur) fd.append("chauffeur_file", files.chauffeur);
      await publicApi.post("/driver-applications", fd);
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit your application — please try again");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-[#F5F2EB]">
      <Navbar/>
      <div className="max-w-2xl mx-auto px-6 pt-32 pb-24">
        {done ? (
          <div className="text-center mt-16" data-testid="application-success">
            <CheckCircle2 size={56} className="mx-auto text-[#D4AF37] mb-6"/>
            <h1 className="font-serif text-4xl mb-4">Application received</h1>
            <p className="text-sm text-[#A1A1AA] max-w-md mx-auto leading-relaxed">
              Thank you for applying to drive with Delux Limo Trans Group. Dispatch will review
              your documents and contact you at the phone number you provided. Once approved,
              you'll receive your onboarding packet (W-9 + contractor agreement) and a secure
              link to set up direct deposit.
            </p>
            <Link to="/" className="btn-gold inline-block mt-10 !px-8" data-testid="back-home-btn">Back to home</Link>
          </div>
        ) : (
          <>
            <div className="text-center mb-10">
              <div className="overline text-[#D4AF37] mb-3">Join the Team</div>
              <h1 className="font-serif text-4xl sm:text-5xl mb-4">Drive with us</h1>
              <p className="text-sm text-[#A1A1AA] max-w-lg mx-auto leading-relaxed">
                Professional chauffeurs earn weekly with Delux Limo Trans Group. Fill out the
                application below — dispatch reviews every application personally.
              </p>
              <p className="text-xs text-[#71717a] mt-4">
                Already drive or dispatch with us?{" "}
                <Link to="/login" className="text-[#D4AF37] hover:underline" data-testid="staff-signin-link">Sign in here</Link>
              </p>
            </div>

            <form onSubmit={submit} className="space-y-8" data-testid="driver-application-form">
              {/* About you */}
              <section className="border border-white/10 bg-[#F7F5F0] text-[#18181b] rounded p-6">
                <h2 className="text-base md:text-lg font-semibold mb-5 flex items-center gap-2">
                  <ShieldCheck size={16} className="text-[#B08D1E]"/> About you
                </h2>
                <div className="space-y-4">
                  <div>
                    <label className={labelCls}>Full name *</label>
                    <input data-testid="app-full-name" required className={inputCls} value={form.full_name} onChange={set("full_name")} placeholder="John Smith"/>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className={labelCls}>Phone number *</label>
                      <input data-testid="app-phone" required type="tel" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="(414) 555-0123"/>
                    </div>
                    <div>
                      <label className={labelCls}>Email *</label>
                      <input data-testid="app-email" required type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@email.com"/>
                    </div>
                  </div>
                </div>
              </section>

              {/* Vehicle */}
              <section className="border border-white/10 bg-[#F7F5F0] text-[#18181b] rounded p-6">
                <h2 className="text-base md:text-lg font-semibold mb-1 flex items-center gap-2">
                  <Car size={16} className="text-[#B08D1E]"/> Your vehicle
                </h2>
                <p className="text-xs text-[#52525b] mb-5">
                  Vehicles must be no more than {MAX_VEHICLE_AGE} years old — model year {MIN_VEHICLE_YEAR} or newer.
                </p>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>Make *</label>
                    <input data-testid="app-vehicle-make" required className={inputCls} value={form.vehicle_make} onChange={set("vehicle_make")} placeholder="Cadillac"/>
                  </div>
                  <div>
                    <label className={labelCls}>Model *</label>
                    <input data-testid="app-vehicle-model" required className={inputCls} value={form.vehicle_model} onChange={set("vehicle_model")} placeholder="Escalade"/>
                  </div>
                  <div>
                    <label className={labelCls}>Year * ({MIN_VEHICLE_YEAR} or newer)</label>
                    <input data-testid="app-vehicle-year" required type="number" min={MIN_VEHICLE_YEAR} max={CURRENT_YEAR + 1} className={inputCls} value={form.vehicle_year} onChange={set("vehicle_year")} placeholder={String(CURRENT_YEAR)}/>
                  </div>
                  <div>
                    <label className={labelCls}>Color</label>
                    <input data-testid="app-vehicle-color" className={inputCls} value={form.vehicle_color} onChange={set("vehicle_color")} placeholder="Black"/>
                  </div>
                </div>
              </section>

              {/* Documents */}
              <section className="border border-white/10 bg-[#F7F5F0] text-[#18181b] rounded p-6">
                <h2 className="text-base md:text-lg font-semibold mb-1 flex items-center gap-2">
                  <FileCheck2 size={16} className="text-[#B08D1E]"/> Your documents
                </h2>
                <p className="text-xs text-[#52525b] mb-5">Photos or PDFs, up to 10 MB each.</p>
                <div className="space-y-4">
                  <FilePick testid="app-license-file" label="Driver's license *" required file={files.license} onChange={setFile("license")}/>
                  <FilePick testid="app-insurance-file" label="Certificate of insurance *" required file={files.insurance} onChange={setFile("insurance")}/>
                  <FilePick testid="app-chauffeur-file" label="Chauffeur / limo license (optional — you can add it later)" file={files.chauffeur} onChange={setFile("chauffeur")}/>
                </div>
              </section>

              <button data-testid="app-submit-btn" disabled={busy} className="btn-gold w-full !py-4 text-sm">
                {busy ? "Submitting…" : "Submit application"}
              </button>
            </form>
          </>
        )}
      </div>
      <Footer/>
    </div>
  );
}

function FilePick({ label, file, onChange, required, testid }) {
  return (
    <label className="block cursor-pointer">
      <span className={labelCls}>{label}</span>
      <div className={`flex items-center gap-3 border rounded px-3 py-3 text-sm bg-white ${file ? "border-[#B08D1E] text-[#7A5A0F] font-medium" : "border-[#d4d4d8] text-[#52525b] hover:border-[#a1a1aa]"}`}>
        {file ? <CheckCircle2 size={16}/> : <Upload size={16}/>}
        <span className="truncate">{file ? file.name : "Tap to upload — photo or PDF"}</span>
      </div>
      <input data-testid={testid} type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf" className="hidden" onChange={onChange}/>
    </label>
  );
}
